import * as A from 'astronomy-engine';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import { DISTANCE_RANGE } from '../data/distances';
import { coversPlace } from '../live/darksites';
import { radiantAt, solarLongitude, upcomingPeaks, zhrAt, hourlyRate } from '../astro/meteors';
import { BodyState, constellationAt, constellationName, moonPhaseName, riseTransitSet, riseTransitSetFixed } from '../astro/solar';
import { bvToKelvin, kelvinToRgb, starDesignation, starName } from '../data/catalog';
import { ARABIC_STAR_NAMES, BODY_NOTES, SHOWER_PARENTS, constellationMeaning } from '../data/lore';
import { asteroidReminder, bodyReminder, constellationReminder, dsoReminder, starReminder } from '../data/islam';
import { Reminder } from './Reminder';
import { DEG, enuToAltAz, refractEnu, vecToRaDec } from '../lib/math';
import { smallBodyState } from '../astro/smallbodies';
import { displayName, longName, satTimeProblem } from '../live/satellites';
import type { Pass } from '../live/satellites';
import { bestView, infoMinimised, panel, place, previewFrom, reliving, selKey, selection, Selection, setSimTime, settings, showToast, simNow, timeLive, timeVersion, viewMode } from '../state';
import { Sheet, Stat } from './chrome';
import {
  brightnessWords, cap, compassPoint, directionWords, fmtAngle, fmtDate, fmtDateTime, fmtKm, fmtLightYears, fmtMag, fmtNum, fmtRelative,
  fmtTime, whereInSky,
} from './format';
import { Icon } from './icons';
import { downloadPassCalendar } from './calendar';
import { Globe } from './Globe';
import { ArabicFigureInfo, ManzilInfo, MoonStationRow, StarArabicNames } from './ArabicInfo';
import { ArabicTermRow } from './ArabicTerm';
import { BODY_ARABIC, KIND_ARABIC } from '../data/arabicTerms';
import { FigureInfo, SimbadInfo } from './CultureInfo';
import { logKey } from '../live/logbook';
import { nightNow, nights, seeIt, seenIndex, seenOnNight, unsee } from '../live/nights';
import { nightId, nightLabel } from '../live/nightsCore';
import { factClipKeys, nameClipKey } from '../live/names';
import { speak, speaking, stopNarration, unlockNarration } from '../audio/narrator';
import { Vec3, cross, dot, norm } from '../lib/math';
import { cantSeeIt, eyeVerdict } from './DarkCheck';
import { GalleryRow } from './GalleryPanel';
import { HIJRI_MONTHS, fmtHijriShort } from '../astro/hijri';
import { crescentDay, hijriNow, lookingFor, lookingText } from '../live/saudi';
import { CrescentPrayer } from './Crescent';
import { EclipsePrayer, FullMoonNote, PleiadesNote, ShootingStarsNote, VerseNote, WhiteDaysNote } from './IslamNotes';
import { SayArabic } from './SayArabic';

/** Cache keys: recompute rise and set times when the place or the (simulated) time moves on. */
const obsKey = (o: A.Observer) => `${o.latitude.toFixed(3)},${o.longitude.toFixed(3)}`;
const tenMin = () => Math.floor(simNow().getTime() / 600e3);

function useTick(ms = 1000) {
  const [, set] = useState(0);
  useEffect(() => {
    const id = setInterval(() => set((x) => x + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
}

/** The part of the screen not covered by the top bar or an open card (CSS px). */
export function visibleSkyRegion(): { x0: number; y0: number; x1: number; y1: number } {
  const W = window.innerWidth, H = window.innerHeight;
  const el = document.querySelector<HTMLElement>('.sheet');
  if (!el) return { x0: 0, y0: 64, x1: W, y1: H - 110 };
  // offsetLeft/offsetHeight ignore the slide-in transform, so this works while the card animates.
  if (el.offsetWidth < W - 120) return { x0: 0, y0: 64, x1: el.offsetLeft - 8, y1: H - 16 };
  return { x0: 0, y0: 64, x1: W, y1: H - el.offsetHeight - 8 };
}

export function InfoPanel() {
  const sel = selection.value;
  // A card's spoken facts end with the card (or when something else is chosen).
  useEffect(() => () => {
    const k = speaking.peek()?.[0];
    if (k && (k.startsWith('fact-') || k.startsWith('name-'))) stopNarration();
  }, [sel && JSON.stringify(sel)]);
  // Keep the chosen object in the sky left visible by the card.
  useEffect(() => {
    const s = services.peek();
    if (!sel || !s || viewMode.peek() !== 'manual') return;
    const frame = () => {
      const loc = s.engine.locate(sel);
      if (!loc) return;
      const below = loc.enu[2] < Math.sin(-1 * DEG) && !settings.peek().seeThroughGround;
      const r = visibleSkyRegion();
      const p = s.engine.camera.project(loc.enu);
      // Comfortably inside: away from the card's edge and the top bar, not just technically visible.
      const my = Math.max(24, (r.y1 - r.y0) * 0.15), mx = Math.max(24, (r.x1 - r.x0) * 0.08);
      const inside = p && p.x > r.x0 + mx && p.x < r.x1 - mx && p.y > r.y0 + my && p.y < r.y1 - my;
      if (!inside && !(below && s.engine.viewCentre().alt < 12)) s.engine.frameInView(loc.enu, r);
    };
    // Frame once the card's size has settled (it grows as pictures and data arrive), and again if it
    // later changes size enough to cover the object.
    let timer = setTimeout(frame, 180);
    const el = document.querySelector('.sheet');
    const ro = el
      ? new ResizeObserver(() => {
          clearTimeout(timer);
          timer = setTimeout(frame, 150);
        })
      : null;
    if (el && ro) ro.observe(el);
    return () => {
      clearTimeout(timer);
      ro?.disconnect();
    };
  }, [sel && JSON.stringify(sel), infoMinimised.value]);
  if (!sel) return null;
  if (infoMinimised.value) return <InfoPeek sel={sel} />;
  return (
    <Sheet onClose={() => (selection.value = null)} onMinimise={() => (infoMinimised.value = true)} labelledBy="info-title" class="info">
      <InfoBody sel={sel} />
    </Sheet>
  );
}

/** The card made small: the name and where to look, Listen, and a tap to open the whole card again. */
function InfoPeek({ sel }: { sel: Selection }) {
  useTick(1000);
  const s = services.value!;
  const loc = s.engine.locate(sel);
  const title = loc?.label || 'The chosen object';
  const w = loc ? enuToAltAz(refractEnu(loc.enu)) : null;
  // Through the 29th nothing says where the Moon is (src/live/saudi.ts), here as on the whole card.
  const looking = sel.kind === 'body' && sel.id === 'Moon' ? lookingFor(simNow().getTime(), place.value) : null;
  const where = looking ? lookingText(looking) : !w ? '' : w.alt < -0.5 ? 'Below the horizon' : whereInSky(w.alt, w.az);
  const sayKey = nameClipKey(s, sel);
  const factKeys = factClipKeys(s, sel);
  const listenKeys = factKeys.length ? factKeys : sayKey ? [sayKey] : [];
  const listening = !!listenKeys.length && speaking.value?.[0] === listenKeys[0];
  const open = () => (infoMinimised.value = false);
  // Focus moves to the small card, so keyboards and screen readers are not left on the page behind.
  const main = useRef<HTMLButtonElement>(null);
  useEffect(() => main.current?.focus({ preventScroll: true }), []);
  return (
    <div class="info-peek" role="region" aria-label={`${title}: the card, made small`}>
      <button class="peek-main" ref={main} onClick={open} aria-label={`Open the card for ${title}`}>
        <span class="peek-title">{title}</span>
        {where && <span class="peek-sub">{where}</span>}
      </button>
      {listenKeys.length > 0 && (
        <button class="peek-btn" aria-label={listening ? 'Stop listening' : factKeys.length ? `Listen to facts about ${title}` : `Say ${title}`}
          onClick={() => {
            if (listening) return stopNarration();
            unlockNarration();
            void speak(listenKeys, false);
          }}>
          <Icon name={listening ? 'pause' : 'speaker'} size={19} />
        </button>
      )}
      <button class="peek-btn peek-open" aria-label="Open the card" onClick={open}><Icon name="chevronDown" size={18} stroke={2.4} /></button>
      <button class="peek-btn" aria-label="Close" onClick={() => (selection.value = null)}><Icon name="close" size={14} stroke={2.6} /></button>
    </div>
  );
}

function InfoBody({ sel }: { sel: Selection }) {
  useTick(1000);
  void timeVersion.value;
  const s = services.value!;
  const loc = s.engine.locate(sel);
  const where = loc ? (() => { const { alt, az } = enuToAltAz(refractEnu(loc.enu)); return { alt, az }; })() : null;
  const centre = () => {
    if (!loc) return;
    if (viewMode.peek() === 'ar') return;
    s.engine.frameInView(loc.enu, visibleSkyRegion(), undefined, 1.0);
  };
  const sayKey = nameClipKey(s, sel);
  // "Listen": its spoken facts where they exist, else just its name.
  const factKeys = factClipKeys(s, sel);
  const listenKeys = factKeys.length ? factKeys : sayKey ? [sayKey] : [];
  const listening = !!listenKeys.length && speaking.value?.[0] === listenKeys[0];
  // "Seen it" belongs to the night of the time on show (never to one still to come), and only to things up
  // in the sky then (not the Sun); something already ticked can always be taken off.
  const seenKey = simNow().getTime() > Date.now() + 60e3 || (sel.kind === 'body' && sel.id === 'Sun') ? null : logKey(s, sel);
  const seen = seenKey ? seenOnNight(seenKey) : null;
  const canTick = !!seen || (!!where && where.alt > -1);
  const ever = seenKey ? seenIndex.value.get(seenKey) : undefined;
  const firstLine = ever && (!seen || ever.first.id !== seen.id || ever.count > 1)
    ? `You first saw it on ${nightLabel(nightId(ever.first.t, ever.first.place.lon))}${ever.count > 1 ? `, and on ${ever.count - 1} ${ever.count === 2 ? 'night' : 'nights'} since` : ''}`
    : null;
  const toggleSeen = () => {
    if (seen) {
      const before = nights.peek();
      // The phone said no: unsee() has said why, and the sighting stays (Astra's pass 2, N2).
      if (!unsee(nightNow(), seen.id)) return;
      showToast('Taken off this night', 5000, { label: 'Undo', run: () => (nights.value = before) });
    } else seeIt(s, sel);
  };
  const canCentre = viewMode.value !== 'ar' && !!where && (where.alt > -1 || settings.value.seeThroughGround);
  // Naked eye: whether the eye can see it from here, by the test that draws it. Something it should see can be
  // marked "I can’t see it" (tonight, here); something too faint says so.
  const eye = eyeVerdict(s, sel);
  const liveHere = timeLive.value && !previewFrom.value && !reliving.value;
  const canCantSee = !!eye?.visible && liveHere && !seen;
  // The name, what it is, then what you can do with it.
  const header = (title: string, subtitle: string, _icon?: string) => (
    <div class="info-head">
      <div class="info-titles">
        <h2 id="info-title">{title}</h2>
        <p>{subtitle}</p>
        {firstLine && <p class="seen-first"><Icon name="check" size={13} /> {firstLine}</p>}
      </div>
      {(canCentre || listenKeys.length > 0 || (seenKey && canTick) || canCantSee) && (
        <div class="info-actions">
          {canCentre && <button class="info-act" onClick={centre}><Icon name="target" size={18} /> Centre</button>}
          {listenKeys.length > 0 && (
            <button
              class="info-act"
              aria-label={listening ? 'Stop listening' : factKeys.length ? `Listen to facts about ${title}` : `Say ${title}`}
              onClick={() => {
                if (listening) return stopNarration();
                unlockNarration();
                void speak(listenKeys, false);
              }}
            >
              <Icon name={listening ? 'pause' : 'speaker'} size={18} /> {listening ? 'Stop' : factKeys.length ? 'Listen' : 'Say'}
            </button>
          )}
          {seenKey && canTick && (
            <button class={`info-act ${seen ? 'on' : ''}`} aria-pressed={!!seen} aria-label={seen ? `Seen it at ${fmtTime(seen.t)}. Tap to take it off this night` : 'Seen it: add it to this night'} onClick={toggleSeen}>
              <Icon name="check" size={18} /> {seen ? 'Seen' : 'Seen it'}
            </button>
          )}
          {canCantSee && (
            <button class="info-act" onClick={() => cantSeeIt(s, eye!.eff, title)}>
              <EyeOff /> I can’t see it
            </button>
          )}
        </div>
      )}
    </div>
  );
  // Below the horizon: say when it comes up, rather than how far below it is. Not for the Moon on the 29th, the 30th
  // and the 1st, nor a rising that falls on one of them: it gives no rising or setting then (src/live/saudi.ts).
  const risesAt = useMemo(() => (where && where.alt < -0.5 ? nextRise(s, sel) : null), [selKey(sel), tenMin(), where ? where.alt < -0.5 : false]);
  const moonQuiet = sel.kind === 'body' && sel.id === 'Moon' && !!risesAt && (crescentDay(simNow().getTime(), place.value) || crescentDay(risesAt.getTime(), place.value));
  const rises = moonQuiet ? null : risesAt;
  // On the day Saudi Arabia looks for the crescent, only that is said, nothing of where the Moon is (src/live/saudi.ts).
  const looking = sel.kind === 'body' && sel.id === 'Moon' ? lookingFor(simNow().getTime(), place.value) : null;
  const bv = bestView.value;
  const best = bv && bv.key === selKey(sel) && Math.abs(bv.t - simNow().getTime()) > 15 * 60e3 ? bv.t : null;
  const whereRow = where && (
    <>
      <div class="where">
        <div>
          <Icon name="compass" size={16} />
          <span>
            {looking
              ? lookingText(looking)
              : where.alt >= -0.5
              ? whereInSky(where.alt, where.az)
              : !rises
                ? `Below the horizon, ${directionWords(where.az)}`
                : rises.getTime() - simNow().getTime() < 12 * 3600e3
                  ? `Not up yet: rises at ${fmtTime(rises)}, ${fmtRelative(rises.getTime(), simNow().getTime())}`
                  : `Not up now: rises ${fmtDateTime(rises)}`}
          </span>
        </div>
        {looking ? null : where.alt >= -0.5 ? (
          <div class="where-sub tnum">{Math.max(0, Math.round(where.alt))}° up · {compassPoint(where.az)} {fmtNum(where.az, 0)}°</div>
        ) : (
          rises && <div class="where-sub">Below the horizon, {directionWords(where.az)}</div>
        )}
        {best && (
          <button class="best-time" onClick={() => setSimTime(new Date(best), 1)}>
            <Icon name="clock" size={15} /> Best at {fmtTime(best)} · Show me then
          </button>
        )}
      </div>
      {eye && !eye.visible && (
        <div class="note eye-faint">
          <EyeOff size={16} />
          {eye.until === 'darker'
            ? ' Too faint to see with your eyes until the sky is darker.'
            : eye.until === 'higher'
              ? ' Too faint to see with your eyes while it is this low.'
              : ` Too faint to see with your eyes from here ${liveHere ? 'tonight' : 'at this time'}.`}
        </div>
      )}
    </>
  );

  switch (sel.kind) {
    case 'star':
      return <StarInfo index={sel.index} header={header} whereRow={whereRow} />;
    case 'body':
      return <BodyInfo id={sel.id} header={header} whereRow={whereRow} />;
    case 'dso':
      return <DsoInfo index={sel.index} header={header} whereRow={whereRow} />;
    case 'constellation':
      return <ConstellationInfo index={sel.index} header={header} whereRow={whereRow} />;
    case 'satellite':
      return <SatelliteInfo id={sel.id} header={header} whereRow={whereRow} />;
    case 'shower':
      return <ShowerInfo code={sel.code} header={header} whereRow={whereRow} />;
    case 'small':
      return <SmallBodyInfo name={sel.name} header={header} whereRow={whereRow} />;
    case 'arabic':
      return <ArabicFigureInfo index={sel.index} header={header} whereRow={whereRow} />;
    case 'figure':
      return <FigureInfo index={sel.index} header={header} whereRow={whereRow} />;
    case 'simbad':
      return <SimbadInfo o={sel} header={header} whereRow={whereRow} />;
    case 'manzil':
      return <ManzilInfo index={sel.index} header={header} whereRow={whereRow} />;
  }
}

/** When a planet, the Moon, a star or a deep-sky object next rises, if it does within the day. */
function nextRise(s: NonNullable<typeof services.value>, sel: Selection): Date | null {
  const obs = s.engine.frame.observer, now = simNow();
  try {
    if (sel.kind === 'body' && sel.id !== 'Sun') return riseTransitSet(A.Body[sel.id as keyof typeof A.Body], obs, now).rise;
    const dir = sel.kind === 'star' ? s.engine.starDir(sel.index) : sel.kind === 'dso' ? s.engine.dsos[sel.index].dir : null;
    if (!dir) return null;
    const { ra, dec } = vecToRaDec(dir);
    const r = riseTransitSetFixed(ra, dec, obs, now).rise;
    return r && r.getTime() > now.getTime() ? r : null;
  } catch {
    return null;
  }
}

/** An eye struck through: "I can’t see it" and "too faint to see". */
function EyeOff({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width={1.6} stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" /><circle cx="12" cy="12" r="3" /><path d="M4.5 4.5l15 15" />
    </svg>
  );
}

type HeaderFn = (title: string, subtitle: string, icon: string) => preact.JSX.Element;
interface InfoProps { header: HeaderFn; whereRow: preact.JSX.Element | null }

export function RiseSetRow({ rs }: { rs: ReturnType<typeof riseTransitSet> }) {
  if (rs.circumpolar) return <div class="note">Never sets from here: it circles the pole all night.</div>;
  if (rs.neverRises) return <div class="note">Never rises from here.</div>;
  // A rising or setting more than a day and a half away is not found: with a setting found, it rose that long ago and
  // is up now; otherwise it is not today (riseTransitSet).
  return (
    <div class="stats three">
      <Stat label="Rises" value={rs.rise ? <span class="tnum">{fmtTime(rs.rise)}</span> : rs.set ? 'already up' : 'not today'} sub={rs.rise ? fmtRelative(rs.rise.getTime(), simNow().getTime()) : ''} />
      <Stat label="Highest" value={rs.transit ? <span class="tnum">{fmtTime(rs.transit)}</span> : 'unknown'} sub={rs.transitAlt != null ? `${Math.round(rs.transitAlt)}° up` : ''} />
      <Stat label="Sets" value={rs.set ? <span class="tnum">{fmtTime(rs.set)}</span> : 'not today'} sub={rs.set ? fmtRelative(rs.set.getTime(), simNow().getTime()) : ''} />
    </div>
  );
}

function ArabicBlock({ name }: { name: string }) {
  const a = ARABIC_STAR_NAMES[name];
  if (!a) return null;
  return (
    <div class="arabic">
      <div class="row-label">{a.fromArabic === false ? 'Its name in Arabic' : 'Its name comes from the Arabic'}</div>
      <div class="arabic-top">
        <span class="arabic-names">
          <span class="arabic-script" lang="ar" dir="rtl">{a.ar}</span>
          <span class="arabic-tr">{a.tr}</span>
        </span>
        <SayArabic text={a.ar} name={a.tr} />
      </div>
      {a.meaning && <div class="arabic-meaning">“{a.meaning}”</div>}
      {a.note && <div class="arabic-note" dir="auto">{a.note}</div>}
    </div>
  );
}

// ---------- Stars ----------
function StarInfo({ index, header, whereRow }: InfoProps & { index: number }) {
  const s = services.value!;
  const cat = s.engine.stars;
  const info = cat.info.get(index);
  const name = starName(cat, index);
  const desig = starDesignation(cat, index);
  const mag = cat.mag[index];
  const dir = s.engine.starDir(index);
  const { ra, dec } = vecToRaDec(dir);
  const con = constellationName(ra, dec);
  const obsK = obsKey(s.engine.frame.observer);
  const rs = useMemo(() => riseTransitSetFixed(ra, dec, s.engine.frame.observer, simNow()), [index, obsK, tenMin()]);
  const kelvin = Math.round(bvToKelvin(cat.bv[index]) / 100) * 100;
  const colour = kelvin > 10000 ? 'Blue-white' : kelvin > 7500 ? 'White' : kelvin > 6000 ? 'Yellow-white' : kelvin > 5000 ? 'Yellow' : kelvin > 3900 ? 'Orange' : 'Red-orange';
  const ly = info?.ly ?? 0;
  // A distance still disputed, as a range (Deneb: src/data/distances.ts).
  const range = info?.name ? DISTANCE_RANGE[info.name] : undefined;
  return (
    <>
      <StarSpecimen kelvin={kelvin} mag={mag} />
      {header(name, `${info?.name ? desig + ' · ' : ''}Star in ${con}`, 'star')}
      {whereRow}
      {info?.name && starReminder(info.name) && <Reminder text={starReminder(info.name)!} />}
      <div class="stats">
        <Stat label="Brightness" value={`mag ${fmtMag(mag)}`} sub={brightnessWords(mag)} />
        <Stat label="Distance" value={range ? `${fmtNum(range[0])} to ${fmtNum(range[1])} light years` : ly ? fmtLightYears(ly) : 'unknown'} />
        <Stat label="Colour" value={colour} sub={`${info?.spect ? info.spect.replace(/[^A-Za-z0-9.+-]/g, '') + ' · ' : ''}about ${fmtNum(kelvin)} K`} />
      </div>
      <RiseSetRow rs={rs} />
      {settings.value.arabic && info?.name && <ArabicBlock name={info.name} />}
      {(settings.value.arabic || settings.value.skyCulture === 'arabic') && <StarArabicNames index={index} skip={info?.name ? ARABIC_STAR_NAMES[info.name]?.ar : undefined} />}
      {info?.variable && <div class="note">A variable star: its brightness changes over time.</div>}
    </>
  );
}

/** The star as a point of light in its true colour, larger the brighter it is. */
function StarSpecimen({ kelvin, mag }: { kelvin: number; mag: number }) {
  const [r, g, b] = kelvinToRgb(kelvin).map((c) => Math.round(c * 255));
  const core = [r, g, b].map((c) => Math.round(c + (255 - c) * 0.55)).join(',');
  const d = Math.max(12, Math.min(30, 24 - mag * 3));
  return (
    <div class="specimen" aria-hidden="true" style={{ background: `radial-gradient(closest-side, rgba(${r},${g},${b},0.09), transparent)` }}>
      <i
        style={{
          width: `${d}px`,
          height: `${d}px`,
          background: `radial-gradient(circle, rgb(${core}) 0 30%, rgb(${r},${g},${b}) 58%, rgba(${r},${g},${b},0) 74%)`,
          boxShadow: `0 0 ${d * 1.3}px ${d * 0.45}px rgba(${r},${g},${b},0.42), 0 0 ${d * 3.4}px ${d * 1.2}px rgba(${r},${g},${b},0.14)`,
        }}
      />
    </div>
  );
}

// ---------- Sun, Moon, planets ----------
function BodyInfo({ id, header, whereRow }: InfoProps & { id: string }) {
  const s = services.value!;
  const b = s.engine.bodies.find((x) => x.id === id) as BodyState | undefined;
  if (!b) return header(id, 'Working out where it is…', id === 'Moon' ? 'moon' : 'planet');
  const now = simNow();
  const obs = s.engine.frame.observer;
  const rs = useMemo(() => riseTransitSet(A.Body[id as keyof typeof A.Body], obs, now), [id, tenMin(), obsKey(obs)]);
  const km = b.distAu * 149597870.7;
  const lightMin = (km / 299792.458) / 60;
  if (id === 'Moon') return <MoonInfo b={b} header={header} whereRow={whereRow} rs={rs} />;
  // A planet's constellation is how it is found. The Sun and the Moon are given none: nobody needs one to find them,
  // and theirs is nearly always one of astrology's "star signs" (owner, 14 October 2026).
  const { ra, dec } = vecToRaDec(b.dir);
  const at = constellationAt(ra, dec);
  const reminder = bodyReminder(id, at.id, at.name);
  const kind = id === 'Sun' ? 'Our star' : ['Uranus', 'Neptune'].includes(id) ? 'Ice giant planet' : ['Jupiter', 'Saturn'].includes(id) ? 'Gas giant planet' : 'Planet';
  return (
    <>
      <BodyGlobe b={b} />
      {header(id, id === 'Sun' ? kind : `${kind} · in ${at.name}`, id === 'Sun' ? 'sun' : 'planet')}
      {whereRow}
      {reminder && <Reminder text={reminder} />}
      <ArabicTermRow term={BODY_ARABIC[id]} label="Its name in Arabic" />
      <div class="stats">
        <Stat label="Brightness" value={`mag ${fmtMag(b.mag)}`} sub={id === 'Sun' ? 'do not look directly' : brightnessWords(b.mag)} />
        <Stat label="Distance" value={fmtKm(km)} sub={lightMin < 60 ? `light takes ${fmtNum(lightMin, 1)} min` : `light takes ${fmtNum(lightMin / 60, 1)} h`} />
        {id === 'Sun' ? (
          <Stat label="Size" value={fmtAngle(b.angDiamDeg)} sub="across" />
        ) : (
          <Stat label="Disc" value={fmtAngle(b.angDiamDeg)} sub={['Mercury', 'Venus', 'Mars'].includes(id) ? `${Math.round(b.phaseFraction * 100)}% lit` : 'across'} />
        )}
      </div>
      <RiseSetRow rs={rs} />
      {/* With sunrise and sunset: the sun and the moon are signs, not to be worshipped (item 43). */}
      {id === 'Sun' && <VerseNote id="Q-41:37" />}
      <GalleryRow body={id} />
      {id === 'Saturn' && b.ringTilt != null && <div class="note">Rings tilted {fmtNum(Math.abs(b.ringTilt), 1)}° towards us{Math.abs(b.ringTilt) < 4 ? ', nearly edge-on' : ''}.</div>}
      {id === 'Jupiter' && <JupiterMoons />}
      {/* The eclipse prayer while the Sun is eclipsed and up here (item 21). */}
      {id === 'Sun' && s.engine.eclipseLight.sun < 0.9999 && s.engine.frame.toEnu(b.dir)[2] > 0 && <EclipsePrayer solar />}
      <div class="note">{BODY_NOTES[id]}</div>
    </>
  );
}

/** Light and pole directions in a north-up view of the body, as seen from Earth. */
function viewFrame(b: BodyState): { light: [number, number, number]; pole: [number, number, number] } {
  const z: Vec3 = [-b.dir[0], -b.dir[1], -b.dir[2]];
  const y = norm([0 - z[2] * z[0], 0 - z[2] * z[1], 1 - z[2] * z[2]]);
  const x = cross(y, z);
  const P = b.pole ?? [0, 0, 1];
  return {
    light: [dot(b.toSun, x), dot(b.toSun, y), dot(b.toSun, z)],
    pole: [dot(P, x), dot(P, y), dot(P, z)],
  };
}

const TEXTURES: Record<string, string> = {
  Sun: 'sun', Mercury: 'mercury', Venus: 'venus', Mars: 'mars', Jupiter: 'jupiter', Saturn: 'saturn', Uranus: 'uranus', Neptune: 'neptune',
};

function BodyGlobe({ b }: { b: BodyState }) {
  const base = import.meta.env.BASE_URL;
  const f = viewFrame(b);
  if (b.id === 'Moon') {
    return <div class="globe-wrap"><Globe texture={`${base}tex/moon-2k.webp`} kind={2} light={f.light} pole={f.pole} spin={((b.libLon ?? 0) * Math.PI) / 180} rotate={false} /></div>;
  }
  const tex = TEXTURES[b.id];
  if (!tex) return null;
  return (
    <div class="globe-wrap">
      <Globe texture={`${base}tex/planets/${tex}.webp`} kind={b.id === 'Sun' ? 1 : 0} light={f.light} pole={f.pole} ring={b.id === 'Saturn' ? `${base}tex/planets/saturn-ring.png` : undefined} size={b.id === 'Saturn' ? 220 : 168} />
    </div>
  );
}

function JupiterMoons() {
  const jm = A.JupiterMoons(simNow());
  const names: [string, A.StateVector][] = [['Io', jm.io], ['Europa', jm.europa], ['Ganymede', jm.ganymede], ['Callisto', jm.callisto]];
  // Positions relative to Jupiter in the sky plane (east/west only, in Jupiter radii) for a quick sketch.
  const s = services.value!;
  const jup = s.engine.bodies.find((b) => b.id === 'Jupiter')!;
  const d = jup.dir;
  const east = [-d[1], d[0], 0];
  const el = Math.hypot(east[0], east[1]) || 1;
  const R_J = 71492 / 149597870.7;
  return (
    <div class="jupiter">
      <div class="row-label">Galilean moons now (east ←→ west)</div>
      <svg viewBox="-32 -6 64 12" class="jup-svg" aria-hidden="true">
        <line x1="-32" y1="0" x2="32" y2="0" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.2" />
        <circle cx="0" cy="0" r="1.2" fill="#f1dfc4" />
        {(() => {
          // Names sit below the line, or above it when they would run into the neighbour's.
          const placed: { y: number; x0: number; x1: number }[] = [];
          return names
            .map(([n, v]) => ({ n, cx: Math.max(-30, Math.min(30, -((v.x * east[0] + v.y * east[1]) / el) / R_J)) }))
            .sort((a, b) => a.cx - b.cx)
            .map(({ n, cx }) => {
              const half = n.length * 0.72;
              const y = [4.6, -2.6].find((yy) => !placed.some((p) => p.y === yy && cx - half < p.x1 && cx + half > p.x0)) ?? 4.6;
              placed.push({ y, x0: cx - half, x1: cx + half });
              return (
                <g key={n}>
                  <circle cx={cx} cy="0" r="0.55" fill="#fff" />
                  <text x={cx} y={y} font-size="2.4" text-anchor="middle" fill="currentColor" opacity="0.7">{n}</text>
                </g>
              );
            });
        })()}
      </svg>
    </div>
  );
}

/** The night of the full Moon: the Hijri day (from sunset to sunset here) in which the moment of full Moon falls. */
function fullMoonDay(t: number, h: { day: number; month: number; year: number }, pl: Parameters<typeof hijriNow>[1]): boolean {
  const full = A.SearchMoonPhase(180, new Date(t - 2 * 86400e3), 4)?.date.getTime();
  if (full == null) return false;
  const f = hijriNow(full, pl);
  return f.day === h.day && f.month === h.month && f.year === h.year;
}

function MoonInfo({ b, header, whereRow, rs }: InfoProps & { b: BodyState; rs: ReturnType<typeof riseTransitSet> }) {
  const now = simNow();
  const phase = A.MoonPhase(now);
  // The day of the Hijri month first, as Saudi Arabia has it here (it turns at sunset): no age of the Moon, which could
  // seem to contradict it (BUILD-SPEC item 38).
  const pl = place.value;
  const h = hijriNow(now.getTime(), pl);
  // On the 29th, the 30th and the 1st, just "Crescent": no phase name, light or rising and setting (src/live/saudi.ts).
  const crescent = crescentDay(now.getTime(), pl);
  // Through the 29th nothing says where the Moon is, so not its station either.
  const looking = lookingFor(now.getTime(), pl);
  const quarters = useMemo(() => {
    const out: { name: string; date: Date }[] = [];
    let q = A.SearchMoonQuarter(now);
    for (let i = 0; i < 4; i++) {
      // No "New Moon" on the 29th, the 30th or the 1st, where it usually falls (src/live/saudi.ts).
      if (q.quarter !== 0 || !crescentDay(q.time.date.getTime(), pl)) out.push({ name: ['New Moon', 'First quarter', 'Full Moon', 'Last quarter'][q.quarter], date: q.time.date });
      q = A.NextMoonQuarter(q);
    }
    return out;
  }, [Math.floor(now.getTime() / 3600e3)]);
  const km = b.distAu * 149597870.7;
  return (
    <>
      <BodyGlobe b={b} />
      {header('Moon', `Day ${h.day} of ${HIJRI_MONTHS[h.month - 1]} · ${crescent ? 'Crescent' : moonPhaseName(phase)}`, 'moon')}
      {whereRow}
      <ArabicTermRow term={BODY_ARABIC.Moon} label="Its name in Arabic" />
      <div class="stats">
        {!crescent && <Stat label="Lit" value={`${Math.round(b.phaseFraction * 100)}%`} sub={moonPhaseName(phase).toLowerCase()} />}
        <Stat label="Distance" value={fmtKm(km)} sub={km < 370000 ? 'closer than average' : km > 400000 ? 'farther than average' : 'about average'} />
      </div>
      {/* The first three nights of the month: the supplication on seeing the new crescent (BUILD-SPEC item 41). */}
      {h.day <= 3 && <CrescentPrayer />}
      <WhiteDaysNote h={h} />
      {fullMoonDay(now.getTime(), h, pl) && <FullMoonNote />}
      {!crescent && ![rs.rise, rs.set].some((t) => t && crescentDay(t.getTime(), pl)) && <RiseSetRow rs={rs} />}
      <GalleryRow body="Moon" />
      {!looking && <MoonStationRow />}
      <div class="list">
        {quarters.map((q) => (
          <div class="list-row" key={q.name}>
            <span>{q.name}</span>
            <span class="tnum muted quarter-when">{fmtDate(q.date)} · {fmtTime(q.date)}<span>{fmtHijriShort(hijriNow(q.date.getTime(), pl))}</span></span>
          </div>
        ))}
      </div>
      {services.value!.engine.eclipseLight.moon < 0.99 && (
        <div class="note">
          {services.value!.engine.eclipseLight.moon < 0.1
            ? 'A total eclipse of the Moon is under way. The only sunlight reaching it has passed through Earth’s atmosphere, which turns it coppery red: the light of every sunrise and sunset on Earth at once.'
            : 'The Moon is passing through Earth’s shadow: a lunar eclipse is under way.'}
        </div>
      )}
      {/* The eclipse prayer while the Moon is in Earth's umbra and up here (item 21). */}
      {services.value!.engine.moonInUmbra && services.value!.engine.frame.toEnu(b.dir)[2] > 0 && <EclipsePrayer solar={false} />}
      {/* The new crescents, markers of time (item 43): after an eclipse's notes, which are for now. */}
      <VerseNote id="Q-2:189" />
      <div class="note">{BODY_NOTES.Moon}</div>
    </>
  );
}

// ---------- Deep sky ----------
/** Each photo's field (degrees across), credit, and for an observatory's picture the page it comes from. */
type PhotoMeta = Record<string, { fov: number; credit: string; source?: string }>;
let photosP: Promise<PhotoMeta> | null = null;
const loadPhotos = (): Promise<PhotoMeta> =>
  (photosP ??= fetch(`${import.meta.env.BASE_URL}img/messier/messier-photos.json`)
    .then((r) => (r.ok ? (r.json() as Promise<PhotoMeta>) : {}))
    .catch(() => ({})));

/** A photograph of a Messier object, from a sky survey or an observatory, with its credit. */
function DsoPhoto({ id, name }: { id: string; name: string }) {
  const [meta, setMeta] = useState<PhotoMeta[string] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setMeta(null);
    setFailed(false);
    void loadPhotos().then((m) => setMeta(m[id] ?? null));
  }, [id]);
  if (!meta || failed) return null;
  const across = meta.fov >= 1 ? `${fmtNum(meta.fov, 1)}°` : `${Math.round(meta.fov * 60)}′`;
  return (
    <figure class="dso-photo">
      <img src={`${import.meta.env.BASE_URL}img/messier/${id}.webp`} alt={`Photograph of ${name}`} width={512} height={512} loading="lazy" onError={() => setFailed(true)} />
      <figcaption>
        {across} across, north up. {meta.source ? <a href={meta.source} target="_blank" rel="noopener">{meta.credit}</a> : meta.credit}.
      </figcaption>
    </figure>
  );
}

function DsoInfo({ index, header, whereRow }: InfoProps & { index: number }) {
  const s = services.value!;
  const d = s.engine.dsos[index];
  const rs = useMemo(() => riseTransitSetFixed(d.ra, d.dec, s.engine.frame.observer, simNow()), [index, obsKey(s.engine.frame.observer), tenMin()]);
  const how = d.mag == null ? 'a telescope' : d.mag < 4.5 ? 'your eyes under a dark sky' : d.mag < 8 ? 'binoculars' : 'a telescope';
  const con = constellationName(d.ra, d.dec);
  return (
    <>
      {d.m && <DsoPhoto id={`M${d.m}`} name={d.name ?? d.id} />}
      {header(d.name ? `${d.name}` : d.id, `${d.t}${d.m ? ` · ${d.cat !== d.id ? d.cat : `Messier ${d.m}`}` : ''} · in ${con}`, 'eye')}
      {whereRow}
      {dsoReminder(d) && <Reminder text={dsoReminder(d)!} />}
      {d.m === 45 && <PleiadesNote />}
      <ArabicTermRow term={KIND_ARABIC[d.t]} label="In Arabic" />
      <div class="stats">
        <Stat label="Catalogue" value={d.id} sub={d.m ? 'Messier catalogue' : ''} />
        <Stat label="Brightness" value={d.mag != null ? `mag ${fmtMag(d.mag)}` : 'unknown'} sub={`see it with ${how}`} />
        <Stat label="Size" value={d.maj ? `${fmtNum(d.maj, d.maj < 10 ? 1 : 0)}′` : 'unknown'} sub={d.maj && d.maj > 31 ? 'larger than the full Moon' : ''} />
      </div>
      <RiseSetRow rs={rs} />
      <GalleryRow sel={`dso:${d.id}`} />
    </>
  );
}

// ---------- Constellations ----------
/** Where on Earth something at this declination climbs at least 10° up. */
function visibleFrom(dec: number): string {
  const lim = Math.round(dec < 0 ? dec + 80 : dec - 80);
  const fmt = (l: number) => `${Math.abs(l)}°${l >= 0 ? 'N' : 'S'}`;
  return dec < 0 ? `visible south of ${fmt(lim)}` : `visible north of ${fmt(lim)}`;
}
function ConstellationInfo({ index, header, whereRow }: InfoProps & { index: number }) {
  const s = services.value!;
  const c = s.engine.cons.constellations[index];
  const members = s.engine.consGeo.stars[index] ?? [];
  const brightest = [...members].sort((a, b) => s.engine.stars.mag[a] - s.engine.stars.mag[b]).slice(0, 4);
  // Its best-known galaxies, clusters and nebulae: Messier objects and named or otherwise well-known ones, brightest first.
  const notable = s.engine.dsos
    .map((d, k) => ({ d, k }))
    .filter(({ d }) => d.con === c.id && (d.m || d.name || d.known) && d.mag != null && /cluster|galaxy|nebula|remnant|association/i.test(d.t))
    .sort((a, b) => a.d.mag! - b.d.mag!)
    .slice(0, 4)
    .map(({ k }) => k);
  // Best evening month: when its label position crosses the meridian around 9 pm.
  const { ra, dec } = vecToRaDec(s.engine.consGeo.anchors[index]);
  const month = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][
    Math.round(((ra / 15 - 3 + 24) % 24) / 2 + 12) % 12
  ];
  // How high its centre can ever get from here.
  const lat = s.engine.frame.observer.latitude;
  const maxAlt = 90 - Math.abs(lat - dec);
  return (
    <>
      {header(c.name, constellationMeaning(c) ? `${cap(constellationMeaning(c))} · constellation` : 'Constellation', 'star')}
      {whereRow}
      {constellationReminder(c.id) && <Reminder text={constellationReminder(c.id)!} />}
      {settings.value.arabic && c.ar && (
        <div class="arabic">
          <div class="row-label">Its name in Arabic</div>
          <div class="arabic-top">
            <span class="arabic-names">
              <span class="arabic-script" lang="ar" dir="rtl">{c.ar}</span>
              {c.arTr && <span class="arabic-tr">{c.arTr}</span>}
            </span>
            <SayArabic text={c.ar} name={c.arTr} />
          </div>
        </div>
      )}
      <div class="stats">
        {maxAlt < 0 ? (
          <Stat label="From here" value="Never rises" sub={visibleFrom(dec)} />
        ) : (
          <Stat label="Best seen" value={month} sub={maxAlt < 15 ? `only ${Math.round(maxAlt)}° up at best from here` : 'high in the evening'} />
        )}
        <Stat label="Figure" value={`${members.length} stars`} sub={`genitive: ${c.gen}`} />
      </div>
      {notable.length > 0 && (
        <div class="list">
          <div class="row-label">Worth finding here</div>
          {notable.map((k) => {
            const d = s.engine.dsos[k];
            return (
              <button class="list-row btn-row" key={k} onClick={() => (selection.value = { kind: 'dso', index: k })}>
                <span>{d.m ? `M${d.m}` : d.cat}{d.name ? ` · ${d.name}` : ''}</span>
                <span class="tnum muted">{d.t.toLowerCase()}{d.mag != null ? ` · mag ${fmtMag(d.mag)}` : ''}</span>
              </button>
            );
          })}
        </div>
      )}
      {brightest.length > 0 && (
        <div class="list">
          <div class="row-label">Brightest stars</div>
          {brightest.map((i) => (
            <button class="list-row btn-row" key={i} onClick={() => (selection.value = { kind: 'star', index: i })}>
              <span>{starName(s.engine.stars, i)}</span>
              <span class="tnum muted">mag {fmtMag(s.engine.stars.mag[i])}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ---------- Satellites ----------
/** How long ago an orbit was measured, in words: "the last hour", "an hour ago", "5 hours ago", "a day ago", "2 days ago". */
export function orbitAgeWords(epoch: number, now = Date.now()): string {
  const h = (now - epoch) / 3600e3;
  if (h < 1) return 'the last hour';
  if (h < 2) return 'an hour ago';
  if (h < 24) return `${Math.floor(h)} hours ago`;
  if (h < 48) return 'a day ago';
  return `${Math.floor(h / 24)} days ago`;
}

function SatelliteInfo({ id, header, whereRow }: InfoProps & { id: number }) {
  const s = services.value!;
  const meta = s.sats.meta.get(id);
  const st = s.sats.stateOf(id);
  const [passes, setPasses] = useState<Pass[] | null>(null);
  const [passFail, setPassFail] = useState(false);
  useEffect(() => {
    let live = true;
    setPasses(null);
    setPassFail(false);
    s.sats.passes([id], simNow(), 72, 9).then((p) => live && setPasses(p), () => live && setPassFail(true));
    return () => {
      live = false;
    };
  }, [id]);
  if (!meta) return header('Satellite', 'No data', 'satellite');
  const problem = satTimeProblem(simNow(), meta);
  if (problem) {
    return (
      <>
        {header(displayName(meta), longName(meta), 'satellite')}
        <div class="note">
          {problem === 'far'
            ? 'Satellite positions can only be predicted for about two weeks either side of today, so this one is hidden at the time you have chosen.'
            : 'This satellite’s orbit data is too old to show where it is at this time. Connect to the internet to update it.'}
        </div>
        <div class="fine">Orbit data from {orbitAgeWords(meta.epoch)}</div>
      </>
    );
  }
  const year = /^(\d{4})-/.exec(meta.intl)?.[1];
  const kindText = meta.kind === 'station' ? 'Crewed space station' : meta.kind === 'starlink' ? 'Starlink internet satellite' : meta.kind === 'rocket' ? 'Spent rocket stage' : meta.kind === 'debris' ? 'Debris' : 'Satellite';
  const speed = meta.periodMin ? (2 * Math.PI * (6371 + (st?.altKm ?? 500))) / (meta.periodMin * 60) : null;
  const visible = st && st.lit > 0.5 && st.enu[2] >= -0.0087;
  const nextVisible = passes?.find((p) => p.visStart && p.visEnd && p.visEnd > simNow().getTime());
  return (
    <>
      {header(longName(meta), `${kindText}${year ? ` · launched ${year}` : ''}`, 'satellite')}
      {whereRow}
      <ArabicTermRow term={id === 25544 ? KIND_ARABIC.ISS : id === 20580 ? KIND_ARABIC.Hubble : meta.kind === 'station' ? KIND_ARABIC['space station'] : KIND_ARABIC.satellite} label={id === 25544 || id === 20580 ? 'Its name in Arabic' : 'In Arabic'} />
      {/* Below the horizon the line above already says so; up there, whether sunlight makes it visible. */}
      {(!st || st.enu[2] >= -0.0087) && (
        <div class={`status ${visible ? 'good' : ''}`}>
          {st ? (st.lit > 0.5 ? (visible ? `Sunlit: look for a steady moving star, ${brightnessWords(st.mag)}` : 'Sunlit') : 'Above you but in Earth’s shadow, so invisible') : 'Position not available'}
        </div>
      )}
      <div class="stats">
        <Stat label="Height" value={st ? fmtKm(st.altKm) : 'unknown'} sub="above Earth" />
        <Stat label="Distance" value={st ? fmtKm(st.range) : 'unknown'} sub="from you" />
        <Stat label="Speed" value={speed ? `${fmtNum(speed, 1)} km/s` : 'unknown'} sub={`orbit every ${fmtNum(meta.periodMin, 0)} min`} />
      </div>
      <div class="list">
        <div class="row-label">Visible passes (next 3 days)</div>
        {!passes && <div class="muted small">{passFail ? 'Ahillah could not work out its passes just now.' : 'Calculating…'}</div>}
        {passes && !passes.some((p) => p.visStart) && <div class="muted small">No visible passes: it will either be in daylight, in Earth’s shadow or too low.</div>}
        {passes?.filter((p) => p.visStart && p.visEnd).slice(0, 6).map((p) => (
          <div class="pass" key={p.rise}>
            <div class="pass-when tnum">{fmtDateTime(p.visStart!)}</div>
            <div class="pass-path muted">
              {compassPoint(p.visStartAz)} → {compassPoint(p.visEndAz)} · up to {Math.round(p.peakEl)}° · {Math.round((p.visEnd! - p.visStart!) / 60000)} min{p.minMag != null ? ` · mag ${fmtMag(p.minMag)}` : ''}
            </div>
          </div>
        ))}
        <div class="muted small">Orbit data from {orbitAgeWords(meta.epoch)}</div>
      </div>
      {nextVisible && (
        <button class="btn" onClick={() => downloadPassCalendar(displayName(meta), nextVisible)}>
          <Icon name="bell" size={16} /> Add next pass to calendar
        </button>
      )}
      <div class="fine">NORAD {id} · {meta.intl} · orbit data from CelesTrak</div>
    </>
  );
}

// ---------- Comets and asteroids ----------
function SmallBodyInfo({ name, header, whereRow }: InfoProps & { name: string }) {
  const s = services.value!;
  const b = s.small.find(name);
  if (!b) return header(name, 'No orbit available', 'planet');
  const now = simNow();
  const st = s.small.stateOf(name) ?? smallBodyState(b, now);
  const { ra, dec } = vecToRaDec(st.dir);
  const rs = useMemo(() => riseTransitSetFixed(ra, dec, s.engine.frame.observer, now), [name, tenMin(), obsKey(s.engine.frame.observer)]);
  const how = st.mag < 5.5 ? 'your eyes under a dark sky' : st.mag < 9 ? 'binoculars' : st.mag < 13 ? 'a small telescope' : 'a large telescope';
  const con = constellationName(ra, dec);
  const isComet = b.kind === 'comet';
  const perihelion = isComet ? new Date((b.tp - 2440587.5) * 86400000) : null;
  return (
    <>
      {header(name, `${isComet ? 'Comet' : 'Asteroid'} · in ${con}`, isComet ? 'meteor' : 'planet')}
      {whereRow}
      {!isComet && asteroidReminder(name) && <Reminder text={asteroidReminder(name)!} />}
      <ArabicTermRow term={KIND_ARABIC[b.kind]} label="In Arabic" />
      <div class="stats">
        <Stat label="Brightness" value={`mag ${fmtMag(st.mag)}`} sub={`see it with ${how}`} />
        <Stat label="From Earth" value={fmtKm(st.delta * 149597870.7)} sub={`${fmtNum(st.delta, 2)} AU`} />
        <Stat label="From the Sun" value={`${fmtNum(st.r, 2)} AU`} sub={isComet && perihelion ? `closest to the Sun ${fmtDate(perihelion, true)}` : b.kind === 'asteroid' && b.diameterKm ? `${Math.round(b.diameterKm)} km across` : ''} />
      </div>
      <RiseSetRow rs={rs} />
      <GalleryRow body={name} sel={`small:${name}`} />
      <div class="note">{isComet ? 'Comet brightness is hard to predict: they can flare or fade. The tail always points away from the Sun.' : 'Asteroids look like faint stars. Watch over a few nights and you will see this one creep against the background stars.'}</div>
      <div class="fine">Orbit: {isComet ? 'Minor Planet Center (updated daily)' : 'JPL Small-Body Database'}; two-body calculation.</div>
    </>
  );
}

// ---------- Meteor showers ----------
function ShowerInfo({ code, header, whereRow }: InfoProps & { code: string }) {
  const s = services.value!;
  const sh = s.meteors.showers.find((x) => x.code === code);
  if (!sh) return header('Meteor shower', s.meteors.showers.length ? 'Not in this year’s list' : 'Loading…', 'meteor');
  const now = simNow();
  const l = solarLongitude(now);
  const zhr = zhrAt(sh, l);
  const rad = radiantAt(sh, l);
  const enu = s.engine.enuFromRaDec(rad.ra, rad.dec);
  const alt = Math.asin(enu[2]) / DEG;
  const rate = hourlyRate(zhr, alt, sh.r, s.engine.context.nelm);
  const peak = upcomingPeaks([sh], new Date(now.getTime() - 20 * 86400e3), 400)[0];
  const speedWord = sh.v > 55 ? 'fast' : sh.v < 30 ? 'slow' : 'medium-speed';
  return (
    <>
      {header(sh.name, `Meteor shower · ${sh.code}`, 'meteor')}
      {whereRow}
      <ArabicTermRow term={KIND_ARABIC['meteor shower']} label="In Arabic" />
      <div class="stats">
        <Stat label="Now" value={zhr > 0 ? (rate >= 1 ? `~${Math.round(rate)} / hour` : alt > 0 ? '< 1 / hour' : 'radiant set') : 'not active'} sub={zhr > 0 ? `ZHR ${fmtNum(zhr, zhr < 10 ? 1 : 0)} at this date` : ''} />
        <Stat label="Peak" value={peak ? fmtDate(peak.date) : 'unknown'} sub={sh.variable ? 'rate varies year to year' : `ZHR up to ${sh.zhr}`} />
        <Stat label="Speed" value={`${sh.v} km/s`} sub={speedWord} />
      </div>
      {SHOWER_PARENTS[code] && <div class="note">These meteors are dust from {SHOWER_PARENTS[code]}, burning up as Earth passes through its trail.</div>}
      <div class="note">Look anywhere in the sky, ideally 40–60° away from the radiant; the meteors will appear to fly out from it. Rates are best when the radiant is high, usually after midnight.</div>
      <ShootingStarsNote />
      {settings.value.bortle >= 7 && coversPlace(place.value) && (
        <button class="note btn-row go-link" onClick={() => { selection.value = null; panel.value = 'go'; }}>
          From a light-polluted place you will see only the brighter meteors. A darker site can multiply the count several times: <strong>find one nearby</strong>.
        </button>
      )}
      <div class="fine">Data: International Meteor Organization, 2026 Meteor Shower Calendar.</div>
    </>
  );
}
